import type { ReactNode } from 'react'
import { useState } from 'react'

import type { CreateUserRequest } from '@/api/admin-types'
import type { Branch } from '@/api/types'
import { ProblemAlert } from '@/components/error-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { branchDescription } from '@/lib/branch'
import type { ApiError } from '@/lib/problem-details'

import { useAdminBranches, useAdminRoles } from './queries'

/**
 * A field, help text, and its label — the small shell every field on this form shares.
 */
function Field({
  id,
  label,
  required = false,
  hint,
  children,
}: {
  id: string
  label: string
  required?: boolean
  hint?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {required ? (
          <span className="text-danger-text" aria-hidden>
            *
          </span>
        ) : null}
      </Label>
      {children}
      {hint ? <p className="text-xs text-muted">{hint}</p> : null}
    </div>
  )
}

/** Simple, deliberately loose: the server is the authority on what makes a valid username. */
const usernamePattern = /^[a-zA-Z0-9._-]{3,}$/

export interface UserCreateFormProps {
  submitLabel: string
  notice?: ReactNode
  outcome?: ReactNode
  isPending?: boolean
  error?: ApiError | null
  onSubmit: (request: CreateUserRequest) => void
  onDone?: () => void
}

/**
 * Onboarding a staff account.
 *
 * Nothing here issues a credential — the account exists once this is countersigned, and a
 * temporary password is a separate, single-use call made from the account's own detail screen
 * afterwards. Asking for a password here would be a secret sitting in a stored approval request
 * for however long a countersignature takes to arrive.
 */
export function UserCreateForm({
  submitLabel,
  notice,
  outcome,
  isPending = false,
  error = null,
  onSubmit,
  onDone,
}: UserCreateFormProps) {
  const branches = useAdminBranches()
  const roles = useAdminRoles()

  // The server refuses any create-user request carrying a break-glass role — it holds everything,
  // so it always breaches segregation of duties, and creation has no override. Offering it here
  // would be offering a checkbox that can never succeed, so it is withheld and explained instead.
  const offerableRoles = (roles.data ?? []).filter((role) => !role.isBreakGlass)
  const withheldRoles = (roles.data ?? []).filter((role) => role.isBreakGlass)

  const [username, setUsername] = useState('')
  const [fullName, setFullName] = useState('')
  const [employeeCode, setEmployeeCode] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [branchId, setBranchId] = useState('')
  const [isLoanOfficer, setIsLoanOfficer] = useState(false)
  const [isTeller, setIsTeller] = useState(false)
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set())

  const settled = outcome !== undefined && outcome !== null

  const trimmedUsername = username.trim()
  const trimmedName = fullName.trim()
  const trimmedCode = employeeCode.trim()
  const trimmedEmail = email.trim()

  const valid =
    usernamePattern.test(trimmedUsername) &&
    trimmedName !== '' &&
    trimmedCode !== '' &&
    trimmedEmail !== '' &&
    branchId !== ''

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds((current) => {
      const next = new Set(current)
      if (next.has(roleId)) {
        next.delete(roleId)
      } else {
        next.add(roleId)
      }
      return next
    })
  }

  const build = (): CreateUserRequest => ({
    username: trimmedUsername,
    fullName: trimmedName,
    employeeCode: trimmedCode,
    email: trimmedEmail,
    phoneNumber: phoneNumber.trim() === '' ? null : phoneNumber.trim(),
    branchId,
    isLoanOfficer,
    isTeller,
    roleIds: [...selectedRoleIds],
  })

  return (
    <Card className="w-full max-w-3xl">
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>New staff account</CardTitle>
          <p className="mt-0.5 text-xs text-muted">
            Accounts are never shared. One person, one account, one home branch.
          </p>
        </div>
      </CardHeader>

      {settled ? (
        <>
          <CardContent className="space-y-3">{outcome}</CardContent>
          <CardFooter>
            <Button variant="primary" onClick={onDone}>
              Back to the directory
            </Button>
          </CardFooter>
        </>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault()
            if (valid && !isPending) {
              onSubmit(build())
            }
          }}
          noValidate
        >
          <CardContent className="space-y-4">
            {notice}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="new-user-username" label="Username" required hint="Letters, digits, dots, dashes, underscores.">
                <Input
                  id="new-user-username"
                  value={username}
                  autoComplete="off"
                  autoFocus
                  onChange={(event) => setUsername(event.target.value)}
                  aria-invalid={username !== '' && !usernamePattern.test(trimmedUsername)}
                />
              </Field>

              <Field id="new-user-code" label="Employee code" required>
                <Input
                  id="new-user-code"
                  value={employeeCode}
                  autoComplete="off"
                  onChange={(event) => setEmployeeCode(event.target.value)}
                />
              </Field>

              <Field id="new-user-name" label="Full name" required>
                <Input id="new-user-name" value={fullName} onChange={(event) => setFullName(event.target.value)} />
              </Field>

              <Field id="new-user-branch" label="Home branch" required>
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger id="new-user-branch">
                    <SelectValue placeholder="Select a branch…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(branches.data ?? []).map((branch: Branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branchDescription(branch)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field id="new-user-email" label="Email" required>
                <Input
                  id="new-user-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>

              <Field id="new-user-phone" label="Phone (optional)">
                <Input id="new-user-phone" value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} />
              </Field>
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-body">
                <Checkbox checked={isLoanOfficer} onCheckedChange={(checked) => setIsLoanOfficer(checked === true)} />
                Loan officer
              </label>
              <label className="flex items-center gap-2 text-sm text-body">
                <Checkbox checked={isTeller} onCheckedChange={(checked) => setIsTeller(checked === true)} />
                Teller
              </label>
            </div>

            <div className="space-y-1.5">
              <Label>Roles</Label>
              {roles.isPending ? (
                <p className="text-sm text-muted">Loading roles…</p>
              ) : roles.isError ? (
                <ProblemAlert error={roles.error} />
              ) : offerableRoles.length === 0 ? (
                <p className="text-sm text-muted">No role is defined yet.</p>
              ) : (
                <ul className="max-h-56 space-y-1.5 overflow-y-auto">
                  {offerableRoles.map((role) => (
                    <li key={role.roleId}>
                      <label className="flex items-start gap-2 rounded-control border border-border-subtle p-2 text-sm">
                        <Checkbox
                          checked={selectedRoleIds.has(role.roleId)}
                          onCheckedChange={() => toggleRole(role.roleId)}
                          className="mt-0.5"
                        />
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-1.5">
                            <span className="font-medium text-heading">{role.name}</span>
                            {role.forbidsFinancialPermissions ? (
                              <Badge tone="neutral">non-financial only</Badge>
                            ) : null}
                          </span>
                          <span className="block text-xs text-muted">{role.description}</span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-muted">
                A combination the server flags as a segregation-of-duties conflict is refused
                outright at creation — there is no justification field here the way there is when
                roles are changed on an existing account. Create the account with a clean set;
                if a conflict is genuinely unavoidable, record a justified exception afterwards
                from Edit roles on the account&apos;s detail page.
              </p>
              {withheldRoles.length > 0 ? (
                <p className="text-xs text-muted">
                  {withheldRoles.map((role) => role.name).join(', ')} is not offered here: holding
                  every permission, it breaches segregation of duties by construction, and there is
                  no override at creation. If it is ever needed, it is granted from Edit roles on an
                  existing account, where the bypass is recorded and audited.
                </p>
              ) : null}
            </div>

            {!valid ? (
              <Alert tone="info">
                <AlertTitle>Not ready to send</AlertTitle>
                <AlertDescription>
                  Username, full name, employee code, email, and a home branch are all required.
                </AlertDescription>
              </Alert>
            ) : null}

            {error ? <ProblemAlert error={error} /> : null}
          </CardContent>

          <CardFooter>
            <Button type="submit" variant="primary" disabled={!valid || isPending}>
              {isPending ? 'Working…' : submitLabel}
            </Button>
          </CardFooter>
        </form>
      )}
    </Card>
  )
}
